import { useEffect, useMemo, useState } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import { getWorkspacePulse, type WorkspacePulseSnapshot } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeBucketCount, timeRangeToDateRange } from './bucketize'

export interface UseRetrievalHealthModuleParams {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export interface UseRetrievalHealthModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const isFailureAction = (action: string): boolean => {
  return /ERROR|FAILED|SHORTAGE|BLOCKED|TIMEOUT/i.test(action)
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export const useRetrievalHealthModule = ({
  workspaceId,
  timeRange,
  refreshKey,
  now = new Date(),
}: UseRetrievalHealthModuleParams): UseRetrievalHealthModuleResult => {
  const [pulse, setPulse] = useState<WorkspacePulseSnapshot | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!workspaceId) {
      setPulse(null)
      setLoading(false)
      return
    }

    let isMounted = true
    setLoading(true)
    setError(null)

    getWorkspacePulse(workspaceId)
      .then((res) => {
        if (isMounted) {
          setPulse(res)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err)
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [workspaceId, refreshKey])

  return useMemo(() => {
    const bucketCount = timeRangeBucketCount(timeRange)

    if (!pulse) {
      return {
        headline: { value: '—', label: 'no records' },
        sparkline: { points: Array(bucketCount).fill(0), direction: 'up-is-good' },
        records: [],
        loading,
        error,
      }
    }

    const activityItems = pulse.activity?.latest || []
    const totalCount = activityItems.length

    if (totalCount === 0 && (pulse.activity?.last24hCount ?? 0) === 0) {
      return {
        headline: { value: '—', label: 'no records' },
        sparkline: { points: Array(bucketCount).fill(0), direction: 'up-is-good' },
        records: [],
        loading,
        error,
      }
    }

    const failedCount = activityItems.filter((a) => isFailureAction(a.action)).length
    const successRate = totalCount > 0
      ? Math.round(((totalCount - failedCount) / totalCount) * 100)
      : Math.max(0, 100 - (pulse.riskScore ?? 0))

    const { from, to } = timeRangeToDateRange(timeRange, now)
    const validDates = activityItems
      .map((item) => parseDate((item as { createdAt?: unknown }).createdAt))
      .filter((d): d is Date => d !== null && d >= from && d <= to)

    let points: number[]
    let delta: number
    if (validDates.length > 0) {
      points = bucketize(validDates, timeRange, now)
      delta = calculateDelta(points)
    } else {
      // If activity.latest[] items don't carry a usable timestamp field, fall back to returning points: []
      // and let the sparkline render as empty — do NOT synthesize values.
      points = []
      delta = 0
    }

    const topRecords: LiveModuleRecord[] = activityItems
      .slice(0, 5)
      .map((item, index) => {
        const primary = item.action
          ? item.action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
          : 'Context Retrieval'
        const secondary = item.summary || 'Retrieved context node'
        const isFail = isFailureAction(item.action)
        return {
          id: item.id || `retrieval-item-${index}`,
          primary,
          secondary: isFail ? `Blocked · ${secondary}` : `Healthy · ${secondary}`,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = {
      value: `${successRate}%`,
      delta,
      label: 'health',
    }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records: topRecords,
      loading,
      error,
    }
  }, [pulse, timeRange, now, loading, error])
}
